import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToId } from "@/lib/scroll";
import { getCompanies } from "@/data/content";
import { UI } from "@/i18n/copy";
import { useLang } from "@/i18n/LanguageContext";

export const CompaniesGrid = () => {
  const { lang } = useLang();
  const t = UI[lang].companies;
  const companies = getCompanies(lang);

  return (
    <section id="companies" data-testid="companies-section" className="scroll-mt-20 bg-zinc-50">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-black/50">
            {t.eyebrow}
          </p>
          <h2 className="mt-6 text-4xl font-black uppercase leading-[1] tracking-tight text-black sm:text-5xl lg:text-6xl">
            {t.headingA}{" "}
            {lang === "en" ? (
              <span className="font-script normal-case tracking-normal">{t.accent}</span>
            ) : (
              t.accent
            )}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-black/55">{t.sub}</p>
        </Reveal>

        <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {companies.map((c, i) => {
            const Icon = c.icon;
            const wide = i === companies.length - 1;
            return (
              <Reveal
                key={c.id}
                delay={(i % 3) * 0.1}
                className={wide ? "md:col-span-2 lg:col-span-3" : ""}
              >
                <article
                  data-testid={`company-card-${c.id}`}
                  className="group relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden rounded-3xl p-8 text-white transition-transform duration-500 ease-out hover:-translate-y-2"
                  style={{ backgroundColor: c.color }}
                >
                  <div
                    className="pointer-events-none absolute -end-16 -top-16 h-48 w-48 rotate-45 bg-white/10 transition-transform duration-700 group-hover:rotate-[60deg]"
                    aria-hidden="true"
                  />
                  <div className="relative">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
                      {c.logo ? (
                        <span
                          className={`inline-flex h-16 shrink-0 items-center rounded-2xl px-4 ${
                            c.logoDark ? "bg-[#1a1a1a]" : "bg-white"
                          }`}
                        >
                          <img
                            src={c.logo}
                            alt={`${c.name} logo`}
                            data-testid={`company-card-logo-${c.id}`}
                            className="max-h-11 w-auto max-w-[175px] object-contain"
                          />
                        </span>
                      ) : (
                        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                          <Icon className="h-6 w-6" />
                        </span>
                      )}
                      <span className="ms-auto max-w-[45%] text-end font-mono-label text-[11px] uppercase leading-relaxed tracking-[0.2em] text-white/70">
                        {c.num} — {c.tagline}
                      </span>
                    </div>
                    <h3 className="mt-8 text-2xl font-black tracking-tight sm:text-3xl">{c.name}</h3>
                    <p className={`mt-3 text-sm leading-relaxed text-white/85 ${wide ? "max-w-2xl" : ""}`}>
                      {c.card}
                    </p>
                  </div>
                  <div className="relative mt-8">
                    <button
                      data-testid={`company-card-cta-${c.id}`}
                      onClick={() => scrollToId(`company-${c.id}`)}
                      className="group/btn inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em] text-black transition-colors duration-300 hover:bg-black hover:text-white"
                    >
                      {t.discover}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 rtl:-scale-x-100" />
                    </button>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
